import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Users as UsersIcon, UserPlus, Trash2, Key, DollarSign, Pencil, Receipt } from 'lucide-react';
import { toast } from 'sonner';
import { ReceiptModal } from '../components/ReceiptModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * ExtraUnitsEditor — for multi-unit residents, admins can add additional units
 * (each with its own property, unit#, monthly fee, share %). Kept collapsed by
 * default so the common single-unit case stays a one-glance form.
 *
 * Props:
 *   - value:   array of { property_id, unit_number, monthly_fee, share_percent, water_meter_number }
 *   - onChange: (nextArray) => void
 *   - properties: [{ _id, name }] — dropdown options
 *   - testIdPrefix: string — e.g. "create-user" or "edit-user"
 */
const ExtraUnitsEditor = ({ value, onChange, properties, testIdPrefix }) => {
  const units = value || [];
  const addUnit = () => {
    onChange([
      ...units,
      { property_id: properties[0]?._id || '', unit_number: '', monthly_fee: '', share_percent: '', water_meter_number: '' },
    ]);
  };
  const updateUnit = (idx, key, val) => {
    const next = units.map((u, i) => (i === idx ? { ...u, [key]: val } : u));
    onChange(next);
  };
  const removeUnit = (idx) => onChange(units.filter((_, i) => i !== idx));

  return (
    <div className="border border-dashed border-[#e2e8f0] rounded-md p-4 bg-[#f8fafc]" data-testid={`${testIdPrefix}-extra-units-block`}>
      <div className="flex items-start justify-between gap-3 mb-3">
        <div>
          <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Additional Units (Optional)</Label>
          <p className="text-xs text-[#64748b] mt-1">
            If this resident owns more than one unit (any property), add each extra unit here. Each row is billed as its own invoice every month.
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={addUnit}
          data-testid={`${testIdPrefix}-add-unit-button`}
        >
          + Add Unit
        </Button>
      </div>

      {units.length === 0 ? (
        <p className="text-xs text-[#94a3b8] italic">No additional units. Click "Add Unit" to bill this resident for another property/unit.</p>
      ) : (
        <div className="space-y-3">
          {units.map((u, idx) => (
            <div
              key={`extra-unit-${idx}`}
              className="grid grid-cols-1 md:grid-cols-12 gap-2 p-3 bg-white border border-[#e2e8f0] rounded-md"
              data-testid={`${testIdPrefix}-extra-unit-row-${idx}`}
            >
              <div className="md:col-span-3 space-y-1">
                <Label className="text-[10px] font-semibold uppercase text-[#64748b]">Property</Label>
                <select
                  value={u.property_id}
                  onChange={(e) => updateUnit(idx, 'property_id', e.target.value)}
                  className="w-full h-9 px-2 rounded-md border border-[#e2e8f0] text-sm bg-white"
                  data-testid={`${testIdPrefix}-extra-unit-property-${idx}`}
                >
                  <option value="">Select...</option>
                  {properties.map(p => (<option key={p._id} value={p._id}>{p.name}</option>))}
                </select>
              </div>
              <div className="md:col-span-2 space-y-1">
                <Label className="text-[10px] font-semibold uppercase text-[#64748b]">Unit #</Label>
                <Input
                  value={u.unit_number}
                  onChange={(e) => updateUnit(idx, 'unit_number', e.target.value)}
                  placeholder="e.g. B-202"
                  className="h-9"
                  data-testid={`${testIdPrefix}-extra-unit-number-${idx}`}
                />
              </div>
              <div className="md:col-span-3 space-y-1">
                <Label className="text-[10px] font-semibold uppercase text-[#64748b]">Monthly Fee (LKR)</Label>
                <Input
                  type="number" step="0.01" min="0"
                  value={u.monthly_fee}
                  onChange={(e) => updateUnit(idx, 'monthly_fee', e.target.value)}
                  placeholder="e.g. 15000"
                  className="h-9"
                  data-testid={`${testIdPrefix}-extra-unit-fee-${idx}`}
                />
              </div>
              <div className="md:col-span-2 space-y-1">
                <Label className="text-[10px] font-semibold uppercase text-[#64748b]">Share %</Label>
                <Input
                  type="number" step="0.0001" min="0" max="100"
                  value={u.share_percent}
                  onChange={(e) => updateUnit(idx, 'share_percent', e.target.value)}
                  placeholder="e.g. 1.25"
                  className="h-9"
                  data-testid={`${testIdPrefix}-extra-unit-share-${idx}`}
                />
              </div>
              <div className="md:col-span-2 flex items-end">
                <Button
                  type="button"
                  size="sm"
                  variant="ghost"
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  onClick={() => removeUnit(idx)}
                  data-testid={`${testIdPrefix}-remove-unit-${idx}`}
                >
                  <Trash2 className="h-4 w-4 mr-1" /> Remove
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export const Users = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'resident',
    phone: '',
    unit_number: '',
    monthly_fee: '',       // per-resident maintenance fee (residents only, required)
    share_percent: '',     // resident's ownership share % (residents only, required, 0-100)
    property_id: '',       // single-property roles
    property_ids: [],      // multi-property roles (supervisor/management)
    // Optional additional units for multi-unit residents. Each row:
    // { property_id, unit_number, monthly_fee, share_percent, water_meter_number }.
    // Left empty for the common single-unit case.
    extra_units: [],
  });

  // Password reset dialog state
  const [resetUserId, setResetUserId] = useState(null);
  const [resetUserName, setResetUserName] = useState('');
  const [newPassword, setNewPassword] = useState('');

  // Per-resident monthly fee editor state
  const [feeEditor, setFeeEditor] = useState(null); // { userId, name, current }
  const [feeInput, setFeeInput] = useState('');

  // Full user editor state
  const [editUser, setEditUser] = useState(null);   // full user object being edited
  const [editSaving, setEditSaving] = useState(false);
  // Receipt browser: { user, receipts, loading } | null. Lets admin pull up
  // any historical receipt for a resident from the user list.
  const [receiptBrowser, setReceiptBrowser] = useState(null);
  const [receiptSubmissionId, setReceiptSubmissionId] = useState(null);

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      const [usersRes, propsRes] = await Promise.all([
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/properties`, { withCredentials: true })
      ]);
      // Sort properties by tower number
      propsRes.data.sort((a, b) => {
        const aNum = parseInt((a.name.match(/\d+/) || [0])[0], 10);
        const bNum = parseInt((b.name.match(/\d+/) || [0])[0], 10);
        return aNum - bNum;
      });
      setUsers(usersRes.data);
      setProperties(propsRes.data);
      if (propsRes.data.length > 0 && !formData.property_id) {
        setFormData(prev => ({ ...prev, property_id: propsRes.data[0]._id }));
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const isMulti = formData.role === 'supervisor' || formData.role === 'management';
    if (isMulti) {
      if (formData.property_ids.length === 0) {
        toast.error(`Assign at least one property to this ${formData.role}`);
        return;
      }
    } else if (!formData.property_id) {
      toast.error('Please select a property');
      return;
    }
    if (formData.role === 'resident' && !formData.unit_number.trim()) {
      toast.error('Unit number is required for residents');
      return;
    }
    if (formData.role === 'resident') {
      const fee = String(formData.monthly_fee).trim();
      if (fee === '' || isNaN(parseFloat(fee)) || parseFloat(fee) < 0) {
        toast.error('Monthly Maintenance Fee is required and must be ≥ 0');
        return;
      }
      const share = String(formData.share_percent).trim();
      if (share === '' || isNaN(parseFloat(share)) || parseFloat(share) < 0 || parseFloat(share) > 100) {
        toast.error('Share % is required and must be between 0 and 100');
        return;
      }
    }
    try {
      const payload = { ...formData };
      if (isMulti) {
        payload.property_id = formData.property_ids[0];  // primary/backward-compat
        payload.property_ids = formData.property_ids;
      } else {
        delete payload.property_ids;
      }
      if (formData.role === 'resident') {
        payload.monthly_fee = parseFloat(formData.monthly_fee);
        payload.share_percent = parseFloat(formData.share_percent);
        // Multi-unit residents: combine the primary form fields with any
        // additional units the admin has added. Only submit `units[]` when
        // there's more than one unit — otherwise the backend uses the classic
        // single-unit path.
        const extras = (formData.extra_units || []).filter(u => u.property_id && String(u.unit_number || '').trim());
        if (extras.length > 0) {
          const primaryUnit = {
            property_id: formData.property_id,
            unit_number: formData.unit_number.trim(),
            monthly_fee: parseFloat(formData.monthly_fee),
            share_percent: parseFloat(formData.share_percent),
            water_meter_number: null,
            is_primary: true,
          };
          payload.units = [primaryUnit, ...extras.map(u => ({
            property_id: u.property_id,
            unit_number: String(u.unit_number).trim(),
            monthly_fee: parseFloat(u.monthly_fee || 0),
            share_percent: parseFloat(u.share_percent || 0),
            water_meter_number: (u.water_meter_number || '').trim() || null,
            is_primary: false,
          }))];
        }
      } else {
        delete payload.monthly_fee;
        delete payload.share_percent;
        delete payload.extra_units;
      }
      // Never send extra_units directly to the API — it's a client-side buffer.
      delete payload.extra_units;
      await axios.post(`${API}/admin/users`, payload, { withCredentials: true });
      toast.success('User created successfully');
      setShowForm(false);
      setFormData({ name: '', email: '', password: '', role: 'resident', phone: '', unit_number: '', monthly_fee: '', share_percent: '', property_id: properties[0]?._id || '', property_ids: [], extra_units: [] });
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create user');
    }
  };

  const openFeeEditor = (u) => {
    setFeeEditor({ userId: u._id, name: u.name, current: u.monthly_fee ?? '' });
    setFeeInput(u.monthly_fee != null ? String(u.monthly_fee) : '');
  };
  const closeFeeEditor = () => { setFeeEditor(null); setFeeInput(''); };
  const submitFeeEditor = async (e) => {
    e.preventDefault();
    const trimmed = feeInput.trim();
    const payload = { monthly_fee: trimmed === '' ? null : parseFloat(trimmed) };
    try {
      await axios.patch(`${API}/admin/users/${feeEditor.userId}/monthly-fee`, payload, { withCredentials: true });
      toast.success('Maintenance fee updated');
      closeFeeEditor();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update fee');
    }
  };

  const openEditUser = (u) => {
    const ids = u.property_ids && u.property_ids.length ? u.property_ids : (u.property_id ? [u.property_id] : []);
    // Multi-unit residents: pull any non-primary units into `extra_units` for
    // the editor. If the resident has exactly one unit (or none), extra_units
    // is empty and the classic single-unit fields drive everything.
    const allUnits = Array.isArray(u.units) ? u.units : [];
    const extras = allUnits
      .filter(x => !x.is_primary)
      .map(x => ({
        property_id: x.property_id || '',
        unit_number: x.unit_number || '',
        monthly_fee: x.monthly_fee != null ? String(x.monthly_fee) : '',
        share_percent: x.share_percent != null ? String(x.share_percent) : '',
        water_meter_number: x.water_meter_number || '',
      }));
    setEditUser({
      _id: u._id,
      name: u.name || '',
      email: u.email || '',
      role: u.role || 'resident',
      phone: u.phone || '',
      unit_number: u.unit_number || '',
      property_id: u.property_id || '',
      property_ids: ids,
      monthly_fee: u.monthly_fee != null ? String(u.monthly_fee) : '',
      share_percent: u.share_percent != null ? String(u.share_percent) : '',
      extra_units: extras,
    });
  };
  const closeEditUser = () => setEditUser(null);
  const submitEditUser = async (e) => {
    e.preventDefault();
    if (!editUser.name.trim()) { toast.error('Name is required'); return; }
    if (!editUser.email.trim()) { toast.error('Email is required'); return; }
    const isMulti = editUser.role === 'supervisor' || editUser.role === 'management';
    if (isMulti && editUser.property_ids.length === 0) {
      toast.error(`Assign at least one property to this ${editUser.role}`);
      return;
    }
    if (!isMulti && !editUser.property_id) {
      toast.error('Select a property');
      return;
    }
    if (editUser.role === 'resident' && !editUser.unit_number.trim()) {
      toast.error('Unit number is required for residents');
      return;
    }
    const payload = {
      name: editUser.name.trim(),
      email: editUser.email.trim().toLowerCase(),
      role: editUser.role,
      phone: editUser.phone.trim(),
    };
    if (editUser.role === 'resident') {
      payload.unit_number = editUser.unit_number.trim();
      const fee = String(editUser.monthly_fee).trim();
      if (fee === '' || isNaN(parseFloat(fee)) || parseFloat(fee) < 0) {
        toast.error('Monthly Maintenance Fee is required and must be ≥ 0');
        return;
      }
      payload.monthly_fee = parseFloat(fee);
      const share = String(editUser.share_percent).trim();
      if (share === '' || isNaN(parseFloat(share)) || parseFloat(share) < 0 || parseFloat(share) > 100) {
        toast.error('Share % is required and must be between 0 and 100');
        return;
      }
      payload.share_percent = parseFloat(share);
      // Multi-unit residents (edit path): if the admin added additional units,
      // combine the primary form fields + extras into `units[]` and let the
      // backend replace the resident's whole unit list.
      const extras = (editUser.extra_units || []).filter(u => u.property_id && String(u.unit_number || '').trim());
      if (extras.length > 0) {
        const primaryUnit = {
          property_id: editUser.property_id,
          unit_number: editUser.unit_number.trim(),
          monthly_fee: parseFloat(fee),
          share_percent: parseFloat(share),
          water_meter_number: null,
          is_primary: true,
        };
        payload.units = [primaryUnit, ...extras.map(u => ({
          property_id: u.property_id,
          unit_number: String(u.unit_number).trim(),
          monthly_fee: parseFloat(u.monthly_fee || 0),
          share_percent: parseFloat(u.share_percent || 0),
          water_meter_number: (u.water_meter_number || '').trim() || null,
          is_primary: false,
        }))];
      }
    }
    if (isMulti) {
      payload.property_ids = editUser.property_ids;
    } else {
      payload.property_id = editUser.property_id;
    }
    setEditSaving(true);
    try {
      await axios.patch(`${API}/admin/users/${editUser._id}`, payload, { withCredentials: true });
      toast.success('User updated');
      closeEditUser();
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update user');
    } finally {
      setEditSaving(false);
    }
  };


  const handleDelete = async (userId, userName) => {
    if (!window.confirm(`Delete user ${userName}?`)) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`, { withCredentials: true });
      toast.success('User deleted');
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete user');
    }
  };

  const openResetPassword = (u) => {
    setResetUserId(u._id);
    setResetUserName(u.name);
    setNewPassword('');
  };

  const openReceiptBrowser = async (u) => {
    setReceiptBrowser({ user: u, receipts: [], loading: true });
    try {
      const { data } = await axios.get(
        `${API}/admin/residents/${u._id}/receipts`,
        { withCredentials: true }
      );
      setReceiptBrowser({ user: u, receipts: data, loading: false });
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to load receipts');
      setReceiptBrowser(null);
    }
  };

  const closeResetPassword = () => {
    setResetUserId(null);
    setResetUserName('');
    setNewPassword('');
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters long');
      return;
    }
    try {
      await axios.post(
        `${API}/admin/users/${resetUserId}/reset-password`,
        { new_password: newPassword },
        { withCredentials: true }
      );
      toast.success(`Password reset for ${resetUserName}`);
      closeResetPassword();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    }
  };

  const propertyName = (id) => properties.find(p => p._id === id)?.name || '—';

  const roleColors = {
    resident: 'bg-blue-100 text-blue-700',
    owner: 'bg-purple-100 text-purple-700',
    security: 'bg-orange-100 text-orange-700',
    supervisor: 'bg-cyan-100 text-cyan-700',
    management: 'bg-green-100 text-green-700'
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-semibold tracking-tight mb-2" style={{ fontFamily: 'Outfit' }}>User Management</h1>
          <p className="text-sm text-[#64748b]">Create and manage user accounts across all properties</p>
        </div>
        <Button
          onClick={() => setShowForm(!showForm)}
          className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white"
          data-testid="create-user-button"
        >
          <UserPlus className="mr-2 h-4 w-4" />
          Create User
        </Button>
      </div>

      {showForm && (
        <Card className="p-6 bg-white border border-[#e2e8f0]">
          <form onSubmit={handleSubmit} className="space-y-4" data-testid="create-user-form">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Full Name</Label>
                <Input value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} placeholder="Jane Smith" required data-testid="user-name-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Email</Label>
                <Input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} placeholder="user@example.com" required data-testid="user-email-input" />
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Temporary Password</Label>
                <Input type="text" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} placeholder="Minimum 6 characters" required minLength={6} data-testid="user-password-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Role</Label>
                <select value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value })} className="w-full h-10 px-3 rounded-md border border-[#e2e8f0] text-sm bg-white" data-testid="user-role-select">
                  <option value="resident">Resident</option>
                  <option value="owner">Owner</option>
                  <option value="security">Security</option>
                  <option value="supervisor">Supervisor (Maintenance)</option>
                  <option value="management">Management</option>
                </select>
              </div>
            </div>

            {/* Property selector — multi-select checklist for supervisor/management, single dropdown otherwise */}
            {(formData.role === 'supervisor' || formData.role === 'management') ? (
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">
                  Properties Assigned <span className="text-red-500">*</span>
                </Label>
                <p className="text-xs text-[#64748b]">This {formData.role} can operate across all selected properties.</p>
                <div className="max-h-48 overflow-y-auto border border-[#e2e8f0] rounded-md p-3 grid grid-cols-2 md:grid-cols-3 gap-2 bg-white" data-testid="user-properties-multi-list">
                  {properties.length === 0 && <p className="text-xs text-[#64748b] col-span-full">No properties available.</p>}
                  {properties.map(p => {
                    const checked = formData.property_ids.includes(p._id);
                    return (
                      <label
                        key={p._id}
                        className="flex items-center gap-2 text-sm cursor-pointer hover:bg-[#f8fafc] rounded px-2 py-1"
                        data-testid={`user-property-checkbox-${p._id}`}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={() => {
                            setFormData(prev => ({
                              ...prev,
                              property_ids: checked
                                ? prev.property_ids.filter(id => id !== p._id)
                                : [...prev.property_ids, p._id],
                            }));
                          }}
                          className="h-4 w-4 rounded border-[#e2e8f0]"
                        />
                        <span className="truncate">{p.name}</span>
                      </label>
                    );
                  })}
                </div>
                <p className="text-xs text-[#64748b]" data-testid="user-properties-selected-count">
                  {formData.property_ids.length} selected
                </p>
              </div>
            ) : (
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Property *</Label>
                <select
                  value={formData.property_id}
                  onChange={(e) => setFormData({ ...formData, property_id: e.target.value })}
                  className="w-full h-10 px-3 rounded-md border border-[#e2e8f0] text-sm bg-white"
                  required
                  data-testid="user-property-select"
                >
                  <option value="">Select property...</option>
                  {properties.map(p => (
                    <option key={p._id} value={p._id}>{p.name}</option>
                  ))}
                </select>
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Phone (Optional)</Label>
                <Input value={formData.phone} onChange={(e) => setFormData({ ...formData, phone: e.target.value })} placeholder="+1234567890" data-testid="user-phone-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">
                  Unit Number {formData.role === 'resident' ? <span className="text-red-500">*</span> : '(Optional)'}
                </Label>
                <Input
                  value={formData.unit_number}
                  onChange={(e) => setFormData({ ...formData, unit_number: e.target.value })}
                  placeholder="e.g., A-101"
                  data-testid="user-unit-input"
                  required={formData.role === 'resident'}
                />
                {formData.role === 'resident' && (
                  <p className="text-xs text-[#64748b]">Required so parcels, invoices and bookings can be routed to this resident.</p>
                )}
              </div>
            </div>

            {formData.role === 'resident' && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">
                    Monthly Maintenance Fee <span className="text-red-500">*</span>
                  </Label>
                  <div className="relative">
                    <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#64748b] font-medium">LKR</span>
                    <Input
                      type="number"
                      step="0.01"
                      min="0"
                      value={formData.monthly_fee}
                      onChange={(e) => setFormData({ ...formData, monthly_fee: e.target.value })}
                      placeholder="e.g., 15000.00"
                      className="pl-14"
                      data-testid="user-monthly-fee-input"
                      required
                    />
                  </div>
                  <p className="text-xs text-[#64748b]">Charged monthly to this resident.</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">
                    Share Value (%) <span className="text-red-500">*</span>
                  </Label>
                  <div className="relative">
                    <Input
                      type="number"
                      step="0.0001"
                      min="0"
                      max="100"
                      value={formData.share_percent}
                      onChange={(e) => setFormData({ ...formData, share_percent: e.target.value })}
                      placeholder="e.g., 1.2500"
                      className="pr-8"
                      data-testid="user-share-percent-input"
                      required
                    />
                    <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[#64748b] font-medium">%</span>
                  </div>
                  <p className="text-xs text-[#64748b]">Resident's ownership share of the building (0–100%).</p>
                </div>
              </div>
            )}

            {formData.role === 'resident' && (
              <ExtraUnitsEditor
                value={formData.extra_units}
                onChange={(next) => setFormData({ ...formData, extra_units: next })}
                properties={properties}
                testIdPrefix="create-user"
              />
            )}

            <div className="flex gap-3">
              <Button type="submit" className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white" data-testid="submit-user-button">Create Account</Button>
              <Button type="button" variant="outline" onClick={() => setShowForm(false)}>Cancel</Button>
            </div>
          </form>
        </Card>
      )}

      {/* Password Reset Modal */}
      {/* Fee editor modal */}
      {feeEditor && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={closeFeeEditor}>
          <Card className="p-6 bg-white w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()} data-testid="fee-editor-modal">
            <h3 className="text-xl font-semibold mb-1" style={{ fontFamily: 'Outfit' }}>Monthly Maintenance Fee</h3>
            <p className="text-xs text-[#64748b] mb-4">for {feeEditor.name}</p>
            <form onSubmit={submitFeeEditor} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Fee (LKR)</Label>
                <div className="relative">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#64748b] font-medium">LKR</span>
                  <Input
                    type="number"
                    step="0.01"
                    min="0"
                    value={feeInput}
                    onChange={(e) => setFeeInput(e.target.value)}
                    placeholder="Leave empty to use property default"
                    className="pl-14"
                    data-testid="fee-editor-input"
                    autoFocus
                  />
                </div>
                <p className="text-xs text-[#64748b]">Leave empty and save to clear the override.</p>
              </div>
              <div className="flex justify-end gap-2">
                <Button type="button" variant="outline" onClick={closeFeeEditor}>Cancel</Button>
                <Button type="submit" className="bg-[#10b981] hover:bg-[#059669] text-white" data-testid="fee-editor-save-button">Save</Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      {/* Full user edit modal */}
      {editUser && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 overflow-y-auto" onClick={closeEditUser}>
          <Card className="p-6 bg-white w-full max-w-2xl my-8" onClick={(e) => e.stopPropagation()} data-testid="edit-user-modal">
            <h3 className="text-xl font-semibold mb-1" style={{ fontFamily: 'Outfit' }}>Edit User</h3>
            <p className="text-xs text-[#64748b] mb-4">Update this user's details and property assignments.</p>
            <form onSubmit={submitEditUser} className="space-y-4">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Name *</Label>
                  <Input value={editUser.name} onChange={(e) => setEditUser({ ...editUser, name: e.target.value })} required data-testid="edit-user-name-input" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Email *</Label>
                  <Input type="email" value={editUser.email} onChange={(e) => setEditUser({ ...editUser, email: e.target.value })} required data-testid="edit-user-email-input" />
                </div>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Role *</Label>
                  <select
                    value={editUser.role}
                    onChange={(e) => setEditUser({ ...editUser, role: e.target.value })}
                    className="w-full h-10 px-3 rounded-md border border-[#e2e8f0] text-sm bg-white"
                    data-testid="edit-user-role-select"
                  >
                    <option value="resident">Resident</option>
                    <option value="owner">Owner</option>
                    <option value="security">Security</option>
                    <option value="supervisor">Supervisor (Maintenance)</option>
                    <option value="management">Management</option>
                  </select>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Phone</Label>
                  <Input value={editUser.phone} onChange={(e) => setEditUser({ ...editUser, phone: e.target.value })} placeholder="+1234567890" data-testid="edit-user-phone-input" />
                </div>
              </div>

              {/* Property assignment — single dropdown or multi checklist based on role */}
              {(editUser.role === 'supervisor' || editUser.role === 'management') ? (
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Properties Assigned *</Label>
                  <div className="max-h-40 overflow-y-auto border border-[#e2e8f0] rounded-md p-3 grid grid-cols-2 md:grid-cols-3 gap-2 bg-white" data-testid="edit-user-properties-multi">
                    {properties.map(p => {
                      const checked = editUser.property_ids.includes(p._id);
                      return (
                        <label key={p._id} className="flex items-center gap-2 text-sm cursor-pointer hover:bg-[#f8fafc] rounded px-2 py-1">
                          <input
                            type="checkbox"
                            checked={checked}
                            onChange={() => {
                              setEditUser(prev => ({
                                ...prev,
                                property_ids: checked
                                  ? prev.property_ids.filter(id => id !== p._id)
                                  : [...prev.property_ids, p._id],
                              }));
                            }}
                            className="h-4 w-4 rounded border-[#e2e8f0]"
                            data-testid={`edit-user-property-checkbox-${p._id}`}
                          />
                          <span className="truncate">{p.name}</span>
                        </label>
                      );
                    })}
                  </div>
                  <p className="text-xs text-[#64748b]">{editUser.property_ids.length} selected</p>
                </div>
              ) : (
                <div className="space-y-2">
                  <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Property *</Label>
                  <select
                    value={editUser.property_id}
                    onChange={(e) => setEditUser({ ...editUser, property_id: e.target.value })}
                    className="w-full h-10 px-3 rounded-md border border-[#e2e8f0] text-sm bg-white"
                    data-testid="edit-user-property-select"
                    required
                  >
                    <option value="">Select property...</option>
                    {properties.map(p => (<option key={p._id} value={p._id}>{p.name}</option>))}
                  </select>
                </div>
              )}

              {editUser.role === 'resident' && (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">Unit Number *</Label>
                      <Input value={editUser.unit_number} onChange={(e) => setEditUser({ ...editUser, unit_number: e.target.value })} placeholder="e.g., A-101" required data-testid="edit-user-unit-input" />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">
                        Monthly Maintenance Fee <span className="text-red-500">*</span>
                      </Label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 -translate-y-1/2 text-sm text-[#64748b] font-medium">LKR</span>
                        <Input
                          type="number"
                          step="0.01"
                          min="0"
                          value={editUser.monthly_fee}
                          onChange={(e) => setEditUser({ ...editUser, monthly_fee: e.target.value })}
                          placeholder="e.g., 15000.00"
                          className="pl-14"
                          data-testid="edit-user-fee-input"
                          required
                        />
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">
                        Share Value (%) <span className="text-red-500">*</span>
                      </Label>
                      <div className="relative">
                        <Input
                          type="number"
                          step="0.0001"
                          min="0"
                          max="100"
                          value={editUser.share_percent}
                          onChange={(e) => setEditUser({ ...editUser, share_percent: e.target.value })}
                          placeholder="e.g., 1.2500"
                          className="pr-8"
                          data-testid="edit-user-share-input"
                          required
                        />
                        <span className="absolute right-3 top-1/2 -translate-y-1/2 text-sm text-[#64748b] font-medium">%</span>
                      </div>
                    </div>
                  </div>

                  <ExtraUnitsEditor
                    value={editUser.extra_units}
                    onChange={(next) => setEditUser({ ...editUser, extra_units: next })}
                    properties={properties}
                    testIdPrefix="edit-user"
                  />
                </>
              )}

              <div className="flex justify-end gap-2 pt-4 border-t border-[#e2e8f0]">
                <Button type="button" variant="outline" onClick={closeEditUser}>Cancel</Button>
                <Button type="submit" disabled={editSaving} className="bg-[#2563eb] hover:bg-[#1d4ed8] text-white" data-testid="submit-edit-user-button">
                  {editSaving ? 'Saving…' : 'Save Changes'}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}




      {resetUserId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50" onClick={closeResetPassword}>
          <Card className="p-6 bg-white w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()} data-testid="reset-password-modal">
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-md bg-[#f59e0b]/20 flex items-center justify-center">
                <Key className="h-5 w-5 text-[#f59e0b]" />
              </div>
              <div>
                <h3 className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>Reset Password</h3>
                <p className="text-xs text-[#64748b]">for {resetUserName}</p>
              </div>
            </div>
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div className="space-y-2">
                <Label className="text-xs font-semibold uppercase tracking-widest text-[#64748b]">New Password</Label>
                <Input
                  type="text"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="Minimum 6 characters"
                  required
                  minLength={6}
                  data-testid="new-password-input"
                  autoFocus
                />
                <p className="text-xs text-[#64748b]">The user will use this password on their next login.</p>
              </div>
              <div className="flex gap-3 justify-end">
                <Button type="button" variant="outline" onClick={closeResetPassword}>Cancel</Button>
                <Button type="submit" className="bg-[#f59e0b] hover:bg-[#d97706] text-white" data-testid="confirm-reset-password-button">
                  Reset Password
                </Button>
              </div>
            </form>
          </Card>
        </div>
      )}

      {/* Users List */}
      {loading ? (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2563eb] mx-auto"></div>
        </div>
      ) : users.length === 0 ? (
        <Card className="p-12 text-center bg-white border border-[#e2e8f0]">
          <UsersIcon className="h-12 w-12 text-[#64748b] mx-auto mb-4" />
          <p className="text-[#64748b]">No users yet</p>
        </Card>
      ) : (
        <Card className="bg-white border border-[#e2e8f0] overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#f8fafc] border-b border-[#e2e8f0]">
                <tr>
                  <th className="text-left p-4 text-xs font-semibold uppercase tracking-widest text-[#64748b]">Name</th>
                  <th className="text-left p-4 text-xs font-semibold uppercase tracking-widest text-[#64748b]">Email</th>
                  <th className="text-left p-4 text-xs font-semibold uppercase tracking-widest text-[#64748b]">Role</th>
                  <th className="text-left p-4 text-xs font-semibold uppercase tracking-widest text-[#64748b]">Properties</th>
                  <th className="text-left p-4 text-xs font-semibold uppercase tracking-widest text-[#64748b]">Unit</th>
                  <th className="text-right p-4 text-xs font-semibold uppercase tracking-widest text-[#64748b]">Monthly Fee</th>
                  <th className="text-right p-4 text-xs font-semibold uppercase tracking-widest text-[#64748b]">Share %</th>
                  <th className="text-right p-4 text-xs font-semibold uppercase tracking-widest text-[#64748b]">Actions</th>
                </tr>
              </thead>
              <tbody>
                {users.map((u, index) => (
                  <tr key={u._id} className="border-b border-[#e2e8f0] last:border-b-0 hover:bg-[#f8fafc]" data-testid={`user-row-${index}`}>
                    <td className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="h-9 w-9 rounded-full bg-[#2563eb] flex items-center justify-center text-white font-semibold text-sm">
                          {u.name?.charAt(0).toUpperCase()}
                        </div>
                        <span className="font-medium text-sm">{u.name}</span>
                      </div>
                    </td>
                    <td className="p-4 text-sm text-[#64748b]">{u.email}</td>
                    <td className="p-4">
                      <span className={`px-3 py-1 text-xs font-medium rounded-full capitalize ${roleColors[u.role] || 'bg-gray-100 text-gray-700'}`}>
                        {u.role}
                      </span>
                    </td>
                    <td className="p-4 text-sm text-[#64748b]" data-testid={`user-property-cell-${index}`}>
                      {(() => {
                        const ids = u.property_ids && u.property_ids.length ? u.property_ids : (u.property_id ? [u.property_id] : []);
                        if (ids.length === 0) return '—';
                        if (ids.length === 1) return propertyName(ids[0]);
                        // Show first + "and N more" pill with title tooltip
                        const names = ids.map(propertyName);
                        return (
                          <span title={names.join(', ')}>
                            {names[0]} <span className="px-1.5 py-0.5 rounded-full bg-cyan-100 text-cyan-700 text-[10px] font-semibold ml-1">+{ids.length - 1} more</span>
                          </span>
                        );
                      })()}
                    </td>
                    <td className="p-4 text-sm text-[#64748b]">
                      {u.unit_number || '—'}
                      {Array.isArray(u.units) && u.units.length > 1 && (
                        <span
                          className="ml-2 px-1.5 py-0.5 rounded-full bg-indigo-100 text-indigo-700 text-[10px] font-semibold"
                          title={u.units.map(x => `${x.unit_number}${x.is_primary ? ' (primary)' : ''}`).join(', ')}
                          data-testid={`user-multi-unit-badge-${index}`}
                        >
                          +{u.units.length - 1} more
                        </span>
                      )}
                    </td>
                    <td className="p-4 text-sm text-right" data-testid={`user-fee-cell-${index}`}>
                      {u.role === 'resident' ? (
                        u.monthly_fee != null ? (
                          <span className="font-medium text-[#0f172a]">LKR {Number(u.monthly_fee).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                        ) : (
                          <span className="text-xs text-[#94a3b8] italic">property default</span>
                        )
                      ) : (
                        <span className="text-[#94a3b8]">—</span>
                      )}
                    </td>
                    <td className="p-4 text-sm text-right" data-testid={`user-share-cell-${index}`}>
                      {u.role === 'resident' ? (
                        u.share_percent != null ? (
                          <span className="font-medium text-[#0f172a]">{Number(u.share_percent).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 })}%</span>
                        ) : (
                          <span className="text-xs text-[#94a3b8] italic">—</span>
                        )
                      ) : (
                        <span className="text-[#94a3b8]">—</span>
                      )}
                    </td>
                    <td className="p-4 text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openEditUser(u)}
                          className="text-[#2563eb] hover:bg-blue-50"
                          data-testid={`edit-user-${index}`}
                          title="Edit user"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        {u.role === 'resident' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openFeeEditor(u)}
                            className="text-[#10b981] hover:bg-green-50"
                            data-testid={`edit-fee-${index}`}
                            title="Edit monthly maintenance fee"
                          >
                            <DollarSign className="h-4 w-4" />
                          </Button>
                        )}
                        {u.role === 'resident' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => openReceiptBrowser(u)}
                            className="text-purple-600 hover:bg-purple-50"
                            data-testid={`view-receipts-${index}`}
                            title="View payment receipts"
                          >
                            <Receipt className="h-4 w-4" />
                          </Button>
                        )}
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => openResetPassword(u)}
                          className="text-[#f59e0b] hover:bg-orange-50"
                          data-testid={`reset-password-${index}`}
                          title="Reset password"
                        >
                          <Key className="h-4 w-4" />
                        </Button>
                        {u._id !== user?._id && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDelete(u._id, u.name)}
                            className="text-[#ef4444] hover:bg-red-50"
                            data-testid={`delete-user-${index}`}
                            title="Delete user"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Receipt browser — list all payment receipts for one resident. */}
      {receiptBrowser && (
        <div
          className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4"
          onClick={() => setReceiptBrowser(null)}
          data-testid="receipt-browser-overlay"
        >
          <Card
            className="p-6 bg-white w-full max-w-2xl max-h-[90vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
            data-testid="receipt-browser-modal"
          >
            <div className="flex items-start justify-between mb-4">
              <div>
                <h3 className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>
                  Payment receipts
                </h3>
                <p className="text-xs text-[#64748b] mt-0.5">
                  {receiptBrowser.user.name} · {receiptBrowser.user.email}
                </p>
              </div>
              <button
                type="button"
                onClick={() => setReceiptBrowser(null)}
                className="text-[#94a3b8] hover:text-[#0f172a]"
                aria-label="Close"
                data-testid="close-receipt-browser"
              >
                ×
              </button>
            </div>
            <div className="flex-1 overflow-y-auto">
              {receiptBrowser.loading ? (
                <div className="text-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2563eb] mx-auto mb-3" />
                  <p className="text-sm text-[#64748b]">Loading receipts…</p>
                </div>
              ) : receiptBrowser.receipts.length === 0 ? (
                <p className="text-sm text-[#94a3b8] italic text-center py-8">
                  No approved payments recorded for this resident yet.
                </p>
              ) : (
                <div className="divide-y divide-[#f1f5f9]">
                  {receiptBrowser.receipts.map((r) => (
                    <div
                      key={r.submission_id}
                      className="flex items-center justify-between py-3 gap-3"
                      data-testid={`receipt-list-item-${r.submission_id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <p className="text-sm">
                          <strong className="text-[#0f172a]">
                            LKR {Number(r.amount).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </strong>
                          {' · '}
                          <span className="text-[#334155]">{r.method_label}</span>
                        </p>
                        <p className="text-xs text-[#64748b] mt-0.5">
                          {new Date(r.payment_date).toLocaleString('en-GB', { timeZone: 'Asia/Colombo' })}
                          {r.receipt_number ? ` · ${r.receipt_number}` : ''}
                          {r.receipt_emailed_at ? ' · emailed' : ''}
                        </p>
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setReceiptSubmissionId(r.submission_id)}
                        className="text-[#2563eb] border-[#2563eb]/40 hover:bg-[#2563eb]/5"
                        data-testid={`open-receipt-${r.submission_id}`}
                      >
                        <Receipt className="mr-1 h-4 w-4" />
                        Receipt
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </Card>
        </div>
      )}

      {receiptSubmissionId && (
        <ReceiptModal
          submissionId={receiptSubmissionId}
          onClose={() => {
            setReceiptSubmissionId(null);
            // Refresh the list so a newly-emailed receipt shows the pill.
            if (receiptBrowser?.user) openReceiptBrowser(receiptBrowser.user);
          }}
        />
      )}
    </div>
  );
};
